/**
 * Builds the plan for `complete` — mark daily stops as serviced. A single
 * `BULK_COMPLETE` call; completion changes work-order status only, so there's
 * no resequence or master leg. Shared by both strategies (the v1 bulk endpoint
 * IS the v2 endpoint here — there's only one way to complete).
 *
 * Empty selection short-circuits to preflight only so the caller never claims
 * work that wasn't done.
 */
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'

import { makeBulkCompleteStep } from '../steps/bulkComplete'
import { makePreflightStep } from '../steps/preflight'
import { makeVerifyStep } from '../steps/verify'

import type { CompleteIntent, Deps, OperationPlan, PipelineStep } from 'types/modules/routing'

const STRATEGY_ID = 'v2-pipeline' as const

const collectWorkOrderIds = (intent: CompleteIntent): ReadonlyArray<string> => {
  const ids: string[] = []
  const seen = new Set<string>()
  intent.stops.forEach(stop => {
    if (isDailyStop(stop) || isOnRequestStop(stop)) {
      const id = String(stop.workOrderId)
      if (id && !seen.has(id)) {
        seen.add(id)
        ids.push(id)
      }
    }
  })
  return ids
}

export const buildCompletePipeline = (intent: CompleteIntent, deps: Deps): OperationPlan => {
  const workOrderIds = collectWorkOrderIds(intent)
  if (workOrderIds.length === 0) {
    return { strategy: STRATEGY_ID, intent, steps: [makePreflightStep({ intent }, deps) as PipelineStep] }
  }
  return {
    strategy: STRATEGY_ID,
    intent,
    steps: [
      makePreflightStep({ intent }, deps) as PipelineStep,
      makeBulkCompleteStep({ body: { status: intent.status, workOrderIds: [...workOrderIds] } }, deps) as PipelineStep,
      makeVerifyStep({ intent }, deps) as PipelineStep,
    ],
  }
}
